'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Box, Button, Typography } from '@/lib/mui';
import { deleteProfileAction } from '@/actions/deleteProfileAction';
import useLogout from '@/hooks/useLogout';
import WhiteNarvarLogo from './icons/WhiteNarvarLogo';
import ScreenLoader from './ScreenLoader';
import CustomDialog from './CustomDialog';
import { useServerActionMutation } from '../hooks/useServerActionMutation';

const flexCenterStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
};

const linkStyle: React.CSSProperties = {
  textDecoration: 'none',
  color: '#E0E0E0',
};

const Divider = <span style={{ margin: '0 5px' }}>|</span>;

export default function Footer({
  isDeleteProfileDialogTriggered,
}: {
  isDeleteProfileDialogTriggered: boolean;
}) {
  const { trigger: logout } = useLogout();
  const [isDeleteProfileDialogOpen, setIsDeleteProfileDialogOpen] = useState(
    isDeleteProfileDialogTriggered,
  );

  const { mutate, isPending, error } = useServerActionMutation(
    deleteProfileAction,
    {
      onSuccess: () => {
        setIsDeleteProfileDialogOpen(false);

        logout();
      },
    },
  );

  const deleteProfileCallback = () => {
    mutate(null);
  };

  const buttons = (
    <>
      <Button
        autoFocus
        onClick={() => setIsDeleteProfileDialogOpen(false)}
        disableElevation
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        color="error"
        autoFocus
        onClick={deleteProfileCallback}
        disableElevation
      >
        Delete
      </Button>
    </>
  );

  return (
    <Box
      color={'#E0E0E0'}
      p={5}
      style={{
        maxHeight: '640px',
        flex: '1 1 0',
        backgroundImage:
          'linear-gradient(180deg, #0096D4 10.94%, #7537FF 63.97%)',
        ...flexCenterStyle,
      }}
    >
      <ScreenLoader open={isPending} />
      <CustomDialog
        open={isDeleteProfileDialogOpen}
        onClose={() => setIsDeleteProfileDialogOpen(false)}
        title="Are you sure?"
        description="Narvar helps you manage your contact information, order history, delivery instructions, and more in one place. Deleting your profile means your data will be permanently erased, and the personalized experiences driven by Narvar will no longer apply. It will be sad to see you go, but you can create a new profile at any time."
        buttons={buttons}
        error={error?.message}
      />
      <WhiteNarvarLogo />
      <Box mt={3} />
      <Typography>
        <Link
          target="_blank"
          style={linkStyle}
          href="https://corp.narvar.com/privacy-policy"
        >
          Privacy Policy{' '}
        </Link>
        {Divider}
        <Link
          target="_blank"
          style={linkStyle}
          href="https://corp.narvar.com/terms"
        >
          Terms
        </Link>
        {Divider}
        <Button
          style={{ ...linkStyle, padding: 0, marginTop: -3 }}
          onClick={() => setIsDeleteProfileDialogOpen(true)}
        >
          <Typography>Delete Profile</Typography>
        </Button>
      </Typography>
    </Box>
  );
}
